/**
 * Просмотр журнала сетевых запросов (.har) из записи бага прямо в карточке:
 * список запросов с отметкой ошибок, поиск по адресу и подробности
 * выбранного запроса — заголовки, тело запроса и ответа.
 *
 * Время у запроса — от начала записи: его можно сверить с видео той же
 * записи. Журнал скачивается по ссылке «на просмотр» только при открытии.
 */

import { ListTree } from "lucide-react";
import { useMemo, useState } from "react";

import { Button, Checkbox, cn, Input, Modal } from "@/shared/ui";
import { Download } from "@/shared/ui/motionIcons";

import type { Attachment } from "@taskflow/client-core/tasks";
import { type HarEntry, type HarLog, type HarPair, isFailed, parseHar } from "./harLog";
import { useAttachmentText } from "./useAttachmentText";

function offsetLabel(entry: HarEntry, startMs: number): string {
  const ms = Math.max(0, Date.parse(entry.startedDateTime) - startMs);
  const seconds = Math.floor(ms / 1000);
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}.${String(ms % 1000).padStart(3, "0")}`;
}

function shortUrl(raw: string): string {
  try {
    const url = new URL(raw);
    return `${url.host}${url.pathname}${url.search}`;
  } catch {
    return raw;
  }
}

export function AttachmentHar({ file, onDownload }: { file: Attachment; onDownload: () => void }) {
  const [viewing, setViewing] = useState(false);
  const { load, fetchOnce } = useAttachmentText(file.id, parseHar);

  const open = () => {
    setViewing(true);
    void fetchOnce();
  };

  return (
    <>
      <button
        type="button"
        className="flex size-10 shrink-0 items-center justify-center rounded border border-line bg-sunken text-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
        aria-label={`Открыть журнал ${file.filename}`}
        title="Открыть журнал запросов"
        onClick={open}
      >
        <ListTree className="size-4" aria-hidden />
      </button>

      <Modal
        open={viewing}
        onClose={() => setViewing(false)}
        title={file.filename}
        width="xl"
        footer={
          <Button size="sm" variant="secondary" icon={Download} onClick={onDownload}>
            Скачать .har
          </Button>
        }
      >
        {load.kind === "ready" ? (
          <HarTable har={load.value} />
        ) : (
          <p className="py-10 text-center text-[13px] text-faint">
            {load.kind === "failed" ? "Не удалось открыть журнал" : "Загрузка…"}
          </p>
        )}
      </Modal>
    </>
  );
}

export function HarTable({ har }: { har: HarLog }) {
  const [query, setQuery] = useState("");
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);

  const entries = har.log.entries;
  const startMs = Date.parse(har.log.pages?.[0]?.startedDateTime ?? entries[0]?.startedDateTime ?? "") || 0;
  const errors = entries.filter(isFailed).length;
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return entries
      .map((entry, index) => ({ entry, index }))
      .filter(({ entry }) => (!onlyErrors || isFailed(entry)) && (needle === "" || entry.request.url.toLowerCase().includes(needle)));
  }, [entries, query, onlyErrors]);
  const current = selected === null ? undefined : entries[selected];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-48 flex-1">
          <Input
            type="search"
            aria-label="Поиск по адресу"
            placeholder="Поиск по адресу"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <Checkbox
          checked={onlyErrors}
          onChange={setOnlyErrors}
          label={`Только ошибки (${String(errors)})`}
        />
        <span className="text-xs text-faint">Запросов: {entries.length}</span>
      </div>

      <div className="max-h-[45dvh] overflow-auto rounded-md border border-line">
        <table className="w-full min-w-[640px] text-left text-xs">
          <thead className="sticky top-0 bg-sunken text-muted">
            <tr>
              <th className="px-2 py-1.5 font-semibold">Время</th>
              <th className="px-2 py-1.5 font-semibold">Метод</th>
              <th className="px-2 py-1.5 font-semibold">Статус</th>
              <th className="px-2 py-1.5 font-semibold">Адрес</th>
              <th className="px-2 py-1.5 font-semibold">Тип</th>
              <th className="px-2 py-1.5 text-right font-semibold">мс</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ entry, index }) => (
              <tr
                key={index}
                aria-selected={selected === index}
                className={cn(
                  "cursor-pointer border-t border-line hover:bg-sunken",
                  selected === index && "bg-sunken",
                  isFailed(entry) && "text-danger",
                )}
                onClick={() => setSelected(index)}
              >
                <td className="px-2 py-1 tabular-nums">{offsetLabel(entry, startMs)}</td>
                <td className="px-2 py-1 font-semibold">{entry.request.method}</td>
                <td className="px-2 py-1 tabular-nums">{entry._error ?? (entry.response.status || "—")}</td>
                <td className="max-w-0 truncate px-2 py-1" title={entry.request.url}>
                  <button type="button" className="max-w-full truncate text-left" onClick={() => setSelected(index)}>
                    {shortUrl(entry.request.url)}
                  </button>
                </td>
                <td className="px-2 py-1 text-muted">{entry._resourceType ?? ""}</td>
                <td className="px-2 py-1 text-right tabular-nums">
                  {entry.time < 0 ? "—" : Math.round(entry.time)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="py-6 text-center text-xs text-faint">Ничего не найдено</p>}
      </div>

      {current !== undefined && <HarDetails entry={current} />}
    </div>
  );
}

function Pairs({ title, pairs }: { title: string; pairs: HarPair[] }) {
  if (pairs.length === 0) return null;
  return (
    <div>
      <h4 className="mb-1 text-xs font-semibold text-muted">{title}</h4>
      <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-3 gap-y-0.5 text-xs">
        {pairs.map((pair, index) => (
          <div key={`${pair.name}-${String(index)}`} className="contents">
            <dt className="font-semibold text-fg">{pair.name}</dt>
            <dd className="break-all text-muted">{pair.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Body({ title, text }: { title: string; text: string | undefined }) {
  if (text === undefined || text === "") return null;
  let shown = text;
  try {
    shown = JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    // Не JSON — показывается как есть.
  }
  return (
    <div>
      <h4 className="mb-1 text-xs font-semibold text-muted">{title}</h4>
      <pre className="max-h-64 overflow-auto rounded-md bg-sunken p-2 text-[11px] whitespace-pre-wrap break-all text-fg">
        {shown}
      </pre>
    </div>
  );
}

/** Тело ответа: текст, картинка из base64 или пояснение, почему его нет. */
function ResponseBody({ content }: { content: HarEntry["response"]["content"] }) {
  const { text, mimeType, encoding, comment } = content;
  if (text !== undefined && encoding === "base64") {
    return (
      <div>
        <h4 className="mb-1 text-xs font-semibold text-muted">Тело ответа</h4>
        {mimeType.startsWith("image/") ? (
          // data: в <img> безопасен и для SVG: скрипты в картинке не исполняются.
          <img
            src={`data:${mimeType};base64,${text}`}
            alt={`Тело ответа (${mimeType})`}
            className="max-h-64 max-w-full rounded-md border border-line bg-[repeating-conic-gradient(var(--color-sunken)_0_25%,transparent_0_50%)] bg-[length:16px_16px] object-contain"
          />
        ) : (
          <p className="text-xs text-muted">
            Двоичные данные ({mimeType || "тип не указан"}),{" "}
            {String(Math.max(1, Math.round((text.length * 3) / 4 / 1024)))} КБ — сохранены в
            файле .har
          </p>
        )}
      </div>
    );
  }
  return (
    <>
      <Body title="Тело ответа" text={text} />
      {comment !== undefined && <p className="text-xs text-faint">{comment}</p>}
    </>
  );
}

function HarDetails({ entry }: { entry: HarEntry }) {
  return (
    <section aria-label="Подробности запроса" className="flex flex-col gap-3 rounded-md border border-line p-3">
      <p className="text-xs break-all text-fg">
        <b>{entry.request.method}</b> {entry.request.url}
        <span className={cn("ml-2 font-semibold", isFailed(entry) ? "text-danger" : "text-muted")}>
          {entry._error ?? `${String(entry.response.status)} ${entry.response.statusText}`}
        </span>
      </p>
      <Pairs title="Заголовки запроса" pairs={entry.request.headers} />
      <Body title="Тело запроса" text={entry.request.postData?.text} />
      <Pairs title="Заголовки ответа" pairs={entry.response.headers} />
      <ResponseBody content={entry.response.content} />
    </section>
  );
}
